// ============================================================
// esignMobile.ts — (E-sign fixes B) which number a signing code is texted to,
// for a client (Client.mainContactNumber / Client.mobileNumber) or a
// litigation friend (litigationFriendPhone / litigationFriendMobile).
// The agreed design (the backend enforces the same when saving):
//   • the MAIN phone is a UK mobile (07…, +447…, 447…, 00447…) and no
//     different mobile is stored → codes go to the main phone. The UI labels
//     it "(used for e-signature codes)" and hides the separate field
//     (mode MAIN). Saving sends mobile: null — the server uses the main number.
//   • otherwise (a separate mobile that differs, or a main phone that isn't a
//     UK mobile) → the separate "Mobile for e-signature codes" (mode
//     SEPARATE); `landlineNote` when the main phone looks like a landline and
//     no mobile is recorded ("This looks like a landline — codes need a mobile").
// The client card on the case and on the Clients page both use this, so they
// always show the same thing. The UK-mobile rule itself is utils/mobileNumber
// (the server's twin, backend/src/lib/mobileNumber.ts).
// ============================================================
import { normaliseUkMobile as normalise } from './mobileNumber'

export const CODES_LABEL_SUFFIX = '(used for e-signature codes)'
export const CODES_MOBILE_LABEL = 'Mobile for e-signature codes'
export const LANDLINE_NOTE = 'This looks like a landline — codes need a mobile'

// A UK mobile → E.164 (+447700900123); anything else (blank, a landline,
// abroad, nonsense) → null.
export const normaliseUkMobile = (s: string | null | undefined): string | null => {
  const n = normalise(s ?? '')
  return n.ok ? n.e164 : null
}
export const isUkMobile = (s: string | null | undefined): boolean => normaliseUkMobile(s) !== null

export interface CodesMobileView {
  mode: 'MAIN' | 'SEPARATE'
  // The number codes go to (E.164), or null when there isn't one yet. In
  // SEPARATE mode a stored value that isn't a UK mobile comes back as typed.
  number: string | null
  landlineNote: boolean
}

export const codesMobileView = ({ phone, mobile }: { phone: string | null | undefined; mobile: string | null | undefined }): CodesMobileView => {
  const main = normaliseUkMobile(phone)
  const mob = (mobile ?? '').trim()
  const mobE164 = normaliseUkMobile(mob)
  if (main && (!mob || mobE164 === main)) return { mode: 'MAIN', number: main, landlineNote: false }
  return { mode: 'SEPARATE', number: mobE164 ?? (mob || null), landlineNote: !main && !!(phone ?? '').trim() && !mob }
}

// What to send as the codes mobile on save: MAIN → null (the server uses the
// main number); SEPARATE → the typed number (blank → null).
export const codesMobileToSend = (mode: CodesMobileView['mode'], typed: string | null | undefined): string | null =>
  mode === 'MAIN' ? null : ((typed ?? '').trim() || null)
